import React, { forwardRef, useEffect, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";

/**
 * Reusable ReCAPTCHA v2 Component
 * @param {function} onChange - Callback with the Google token when verified, or null when the token is no longer valid
 * @param {React.Ref} ref - Reference to access ReCAPTCHA methods (e.g., reset)
 */
const ReCaptchaComponent = forwardRef(({ onChange, onExpired, theme = "light" }, ref) => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  // Clear the stored token when this widget unmounts so a token solved on one
  // form (e.g. login) is never reused by another (e.g. forgot password).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => () => onChangeRef.current?.(null), []);

  if (!siteKey) {
    // No widget means no token, so login / forgot password stay blocked.
    console.error("reCAPTCHA site key is not defined in environment variables");
    return null;
  }

  const handleExpired = () => {
    console.warn("reCAPTCHA expired - user needs to verify again");
    onChange?.(null); // Drop the expired token so it cannot be submitted
    if (onExpired) {
      onExpired(); // Call parent's expiration handler
    }
  };

  const handleError = () => {
    console.error("reCAPTCHA error occurred");
    onChange?.(null); // Drop any stale token
    // Auto-reset on error
    if (ref?.current) {
      ref.current.reset();
    }
  };

  return (
    <div className="recaptcha-wrapper mb-3">
      <ReCAPTCHA
        ref={ref}
        sitekey={siteKey}
        theme={theme}
        onChange={onChange}
        onErrored={handleError}
        onExpired={handleExpired}
      />
    </div>
  );
});

ReCaptchaComponent.displayName = "ReCaptchaComponent";

export default ReCaptchaComponent;
